import { VIRTUAL_PAGE_PATH_REGEX } from '../../third-party-tracking/resources/constants';
import {
  CustomParams,
  CustomParamsUser,
} from '../../third-party-tracking/google-analytics/ga4Types';
import { getNonEmptyValuesFromObject } from '../../utils/object';
import { gtagWrapper } from '../../third-party-tracking/google-analytics/utils';

/**
 * This function takes a page name as input and extracts parameters from it.
 * It uses a regular expression defined by `VIRTUAL_PAGE_SPLITTER_REGEX` to split the page name and extract the parameters.
 * If the page name does not match the regular expression, it returns an array with two empty strings.
 *
 * @param {string} screenName - The name of the page from which to extract parameters.
 * @returns {RegExpMatchArray} - An array containing the extracted parameters. If no parameters are found, it returns `['', '']`.
 */
export const extractParametersFromPageName = (
  screenName: string
): RegExpMatchArray => {
  return screenName?.match(VIRTUAL_PAGE_PATH_REGEX) || ['', ''];
};

/**
 * Determines whether the flow or the product in the incoming VP is changed.
 *
 * @param {string } virtualPage - The virtual page string to check.
 * @param {CustomDimensionsOrParams} parametersInStorage - The parameters in storage to check in.
 * @returns {boolean} - Returns true if the flow/product changed, false otherwise.
 */
export const isFlowOrProductChanged = (
  virtualPage: string,
  parametersInStorage: CustomParams
): boolean => {
  const currentParameters = extractParametersFromPageName(virtualPage);
  const previousParameters = extractParametersFromPageName(
    parametersInStorage['screen_name']
  );
  const isFlowEqual = currentParameters[1] === previousParameters[1];
  const isProductEqual = currentParameters[2] === previousParameters[2];

  return !isProductEqual || !isFlowEqual;
};

/**
 * Tracks GA4 user properties. If properties are not provided, it will track the properties from storage.
 * Only tracks properties that are not empty.
 */
export const trackUserProperties = (params: CustomParamsUser) => {
  const userParams: CustomParamsUser = getNonEmptyValuesFromObject(params);
  if (!Object.keys(userParams).length) {
    return;
  }
  gtagWrapper('set', 'user_properties', userParams);
};
